import { CollectionType, Pagination, ProductType, RecommendationType, UserType } from '@/@types';
import { gql } from '@/services/api';

export const PRODUCT_DETAILS_FRAGMENT = gql`
  fragment ProductDetails on Product {
    id
    name
    image
    description
    popularity
    media {
      type
      source
    }
    category {
      id
      name
    }
    subcategories {
      label
      name
    }
    recommendations(pagination: $recommendationsPagination, filters: $recommendationFilters) {
      total
    }
    extraInfo {
      ratings {
        source
        value
        unit
        total
        votes
      }
      ... on Restaurant {
        externalId {
          covermanager
        }
        workingHours {
          weekDay
          times {
            from
            to
          }
        }
        location {
          fullAddress
          geo {
            lat
            lng
          }
        }
        phone
        price
        url
      }
      ... on Movie {
        preview {
          source
          id
        }
        releaseDate
        providers {
          id
          name
          url
        }
      }
      ... on Serie {
        preview {
          source
          id
        }
        releaseDate
        providers {
          id
          name
          url
        }
      }
      ... on Place {
        location {
          fullAddress
          geo {
            lat
            lng
          }
        }
        workingHours {
          weekDay
          times {
            from
            to
          }
        }
        phone
        price
        url
      }
      ... on Music {
        album
        duration
        artists {
          id
          name
        }
        providers {
          name
          url
        }
      }
    }
  }
`;

export const RECO_FRAGMENT = gql`
  fragment Recommendation on Recommendation {
    id
    message
    createdAt
    message
    media {
      type
      source
    }
    user {
      id
      nickname
      avatar
      level {
        id
        name
      }
    }
    likes {
      total
    }
  }
`;

export type ProductQueryType = {
  Product: {
    get?: ProductType & {
      /**
       * @deprecated we need this for now because we have recos with and without message and we use it to calculate popularity
       */
      allRecos: { total: number };
    };
  };
  Recommendation: {
    get?: RecommendationType;
  };
};

export type UsersQueryType = {
  User: {
    find: Pagination<UserType>;
  };
  Product: {
    get: { collections: Pagination<CollectionType> };
  };
};

export const lazyExtraInfo = ({ id }: { id: string }) => {
  return {
    query: gql`
      query getProductLazyInfo($id: ID!) {
        Product {
          get(id: $id) {
            collections(
              pagination: { first: 7, sortBy: TOTAL_RECOMMENDATIONS, sortDirection: DESC }
              filters: { user: { levels: [STAR, UNICORN] } }
            ) {
              total
              edges {
                node {
                  id
                  name
                  media {
                    type
                    source
                  }
                  content {
                    total
                  }
                }
              }
            }
          }
        }
        User {
          find(filters: { productsId: [$id], levels: [STAR, UNICORN, INFLUENCER] }, pagination: { first: 10 }) {
            edges {
              node {
                id
                name
                lastname
                nickname
                avatar
                level {
                  id
                  name
                }
                recommendations {
                  total
                }
              }
            }
          }
        }
      }
    `,
    variables: {
      id,
    },
  };
};

export const product = ({ id, recommendationId }: { id: string; recommendationId?: string }) => {
  return {
    query: gql`
      ${PRODUCT_DETAILS_FRAGMENT}
      ${RECO_FRAGMENT}
      query getProduct(
        $id: ID!
        $recommendationsPagination: PaginationStringArgs
        $recommendationFilters: ProductRecommendationFilters = { withMessage: true }
        $hasReferer: Boolean!
        $recommendationId: ID = ""
      ) {
        Product {
          get(id: $id) {
            ...ProductDetails
            related {
              id
              name
              media {
                type
                source
              }
              category {
                id
                name
              }
            }
            allRecos: recommendations {
              total
            }
            recommendations(pagination: $recommendationsPagination, filters: $recommendationFilters) {
              edges {
                node {
                  ...Recommendation
                }
              }
            }
          }
        }
        Recommendation {
          get(id: $recommendationId) @include(if: $hasReferer) {
            ...Recommendation
          }
        }
      }
    `,
    variables: {
      id,
      recommendationId,
      hasReferer: !!recommendationId,
    },
  };
};
